<template>
 <MarketingShell>
 <div class="tm-integrated tm-termsscreen">
  <div class="terms-page" :class="{ 'terms-dark': isDark }">
    <!-- Nav -->
    

    <div class="terms-main">
      <!-- Header -->
      <div class="terms-hero">
        <h1>Terms of Service</h1>
        <p class="hero-subtitle">App usage terms and conditions</p>
      </div>

      <!-- Content -->
      <div class="terms-content">
        <!-- Introduction -->
        <div class="intro-card">
          <h2>Welcome to TradingCandle</h2>
          <p>
            By using TradingCandle, you agree to these terms and conditions. 
            Please read them carefully before using our application.
          </p>
        </div>

        <!-- Acceptance of Terms -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Acceptance of Terms
          </h3>
          <p>
            By downloading, installing, or using TradingCandle, you acknowledge that you have 
            read, understood, and agree to be bound by these Terms of Service.
          </p>
          <p>
            If you do not agree to these terms, you may not use the application.
          </p>
        </section>

        <!-- Authentication and Account -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Authentication and Account
          </h3>
          <p>
            <strong>Clerk Authentication:</strong> We use Clerk as our authentication 
            provider to secure your account and manage access to the application.
          </p>
          <p>
            <strong>Account Security:</strong> You are responsible for maintaining 
            the confidentiality of your login credentials and account access.
          </p>
          <p>
            <strong>Account Termination:</strong> We reserve the right to suspend 
            or terminate accounts that violate these terms or engage in prohibited activities.
          </p>
        </section>

        <!-- App Usage Terms -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            App Usage Terms
          </h3>
          <div class="terms-list">
            <div class="list-item">
              <span class="list-number">1.</span>
              <div class="list-content">
                <strong>Informational Purpose Only:</strong> TradingCandle is provided 
                for informational and educational purposes only and should not be considered as financial advice.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">2.</span>
              <div class="list-content">
                <strong>No Liability for Losses:</strong> We are not responsible for any 
                trading losses, financial damages, or consequences resulting from your use of the application.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">3.</span>
              <div class="list-content">
                <strong>Data Provider Terms:</strong> You must respect and comply with 
                the terms of service of our data providers (Fyers, Binance, Twelve Data).
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">4.</span>
              <div class="list-content">
                <strong>Responsible Use:</strong> Use the application ethically, 
                legally, and in accordance with all applicable laws and regulations.
              </div>
            </div>
          </div>
        </section>

        <!-- AI Services and Features -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
              <path d="M9 14h6"/>
            </svg>
            AI Services and Features
          </h3>
          <p>
            <strong>AI-Powered Features:</strong> TradingCandle provides AI-powered 
            features including chat assistance, indicator generation, and market analysis through external AI providers.
          </p>
          <div class="terms-list">
            <div class="list-item">
              <span class="list-number">1.</span>
              <div class="list-content">
                <strong>No Financial Advice:</strong> AI-generated content is for informational 
                purposes only and does not constitute financial advice, investment recommendations, or trading guidance.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">2.</span>
              <div class="list-content">
                <strong>AI Accuracy:</strong> AI responses may contain inaccuracies, errors, 
                or outdated information. Always verify AI-generated content independently.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">3.</span>
              <div class="list-content">
                <strong>User Responsibility:</strong> You are solely responsible for 
                decisions made based on AI-generated content and analysis.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">4.</span>
              <div class="list-content">
                <strong>Data Usage:</strong> Your interactions with AI features may be 
                processed by external AI services as described in our Privacy Policy.
              </div>
            </div>
          </div>
        </section>

        <!-- User Responsibilities -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            User Responsibilities
          </h3>
          <div class="terms-list">
            <div class="list-item">
              <span class="list-number">1.</span>
              <div class="list-content">
                <strong>Trading Decisions:</strong> You are solely responsible for all 
                trading decisions and their financial consequences.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">2.</span>
              <div class="list-content">
                <strong>Professional Advice:</strong> Consult with qualified financial 
                advisors before making any investment decisions.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">3.</span>
              <div class="list-content">
                <strong>Risk Understanding:</strong> Understand and accept all risks 
                associated with trading and investing.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">4.</span>
              <div class="list-content">
                <strong>Account Security:</strong> Maintain the security of your 
                accounts and API keys used with the application.
              </div>
            </div>
          </div>
        </section>

        <!-- Data and Content -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            </svg>
            Data and Content
          </h3>
          <p>
            <strong>Data Sources:</strong> Market data is provided by third-party 
            services and may contain inaccuracies, delays, or interruptions.
          </p>
          <p>
            <strong>No Warranty:</strong> We do not guarantee the accuracy, 
            completeness, or timeliness of any data or information provided.
          </p>
          <p>
            <strong>Intellectual Property:</strong> All application content, 
            features, and functionality are owned by TradingCandle and protected by intellectual property laws.
          </p>
        </section>

        <!-- Limitations and Disclaimers -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Limitations and Disclaimers
          </h3>
          <div class="warning-box">
            <h4>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Important Disclaimer
            </h4>
            <p>
              TradingCandle is provided "as is" without any warranties. We disclaim all liability for 
              any direct, indirect, or consequential damages arising from the use of this application.
            </p>
          </div>
          <p>
            <strong>No Financial Advice:</strong> The application does not provide 
            financial, investment, or trading advice.
          </p>
          <p>
            <strong>Market Risks:</strong> All trading involves risk of loss. 
            Past performance does not guarantee future results.
          </p>
        </section>

        <!-- Terms Modification -->
        <section class="terms-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
            </svg>
            Terms Modification
          </h3>
          <p>
            We reserve the right to modify these terms at any time. Changes will be effective 
            immediately upon posting in the application.
          </p>
          <p>
            Your continued use of the application after any changes constitutes acceptance 
            of the modified terms.
          </p>
        </section>

        <!-- Website Link -->
        <div class="website-card">
          <h3>View Complete Terms</h3>
          <p>For the complete and detailed Terms of Service, please visit our website.</p>
          <a href="https://tradingcandle.app/terms" target="_blank" rel="noopener" class="btn-website">
            Visit Website
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15,3 21,3 21,9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Footer -->
    
  </div>

 </div>
 </MarketingShell>
</template>

<script>
import MarketingShell from "./marketing/MarketingShell.vue";
export default {
  components: { MarketingShell },
  name: 'TermsScreen',
  computed: {
    isDark() {
      return this.$store.getters.isDarkTheme;
    }
  },
  methods: {
    toggleTheme() {
      this.$store.dispatch('toggleTheme');
    }
  }
}
</script>

<style scoped>
.terms-page {
  min-height: 100vh;
  background: #fafaf8;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0f1923;
  display: flex;
  flex-direction: column;
}

/* Nav */
.terms-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  border-bottom: 1px solid #e8e5de;
  background: rgba(250, 250, 248, 0.95);
  backdrop-filter: blur(8px);
}

.terms-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0f1923;
}

.terms-logo-img {
  height: 32px;
}

.terms-logo-text {
  font-family: 'DM Serif Display', serif;
  font-size: 20px;
  font-weight: 600;
}

.terms-nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme-toggle-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d0ccc4;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.theme-toggle-btn:hover {
  background: #f0ede6;
}

.btn-outline {
  padding: 8px 20px;
  border: 1.5px solid #d0ccc4;
  background: transparent;
  color: #2b3a47;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
}

.btn-outline:hover {
  border-color: #0f1923;
  color: #0f1923;
}

/* Main Layout */
.terms-main {
  flex: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 48px 24px;
  width: 100%;
}

/* Hero Section */
.terms-hero {
  text-align: center;
  margin-bottom: 48px;
}

.terms-hero h1 {
  font-family: 'DM Serif Display', serif;
  font-size: 48px;
  letter-spacing: -1px;
  margin: 0 0 16px;
  font-weight: 600;
  color: #0f1923;
}

.hero-subtitle {
  font-size: 18px;
  color: #4a5568;
  line-height: 1.6;
  max-width: 560px;
  margin: 0 auto;
}

/* Content */
.terms-content {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.intro-card {
  background: linear-gradient(135deg, #2962ff20 0%, #2962ff05 100%);
  border: 1px solid #2962ff30;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;
  text-align: center;
}

.intro-card h2 {
  font-size: 24px;
  font-weight: 700;
  color: #0f1923;
  margin: 0 0 16px;
}

.intro-card p {
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
  margin: 0;
}

/* Sections */
.terms-section {
  margin-bottom: 48px;
}

.terms-section h3 {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  color: #0f1923;
  margin: 0 0 20px;
}

.terms-section h3 svg {
  color: #2962ff;
  flex-shrink: 0;
}

.terms-section p {
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
  margin: 0 0 16px;
}

.terms-section p:last-child {
  margin-bottom: 0;
}

.terms-section strong {
  color: #0f1923;
  font-weight: 600;
}

/* List */
.terms-list {
  margin-top: 20px;
}

.list-item {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.list-number {
  font-size: 16px;
  font-weight: 700;
  color: #2962ff;
  min-width: 20px;
}

.list-content {
  flex: 1;
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
}

.list-content strong {
  color: #0f1923;
  font-weight: 600;
}

/* Warning Box */
.warning-box {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.warning-box h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 12px;
}

.warning-box p {
  font-size: 14px;
  line-height: 1.5;
  color: #92400e;
  margin: 0;
}

/* Website Card */
.website-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 32px;
  margin-top: 48px;
  text-align: center;
}

.website-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: #166534;
  margin: 0 0 12px;
}

.website-card p {
  font-size: 16px;
  color: #15803d;
  margin: 0 0 24px;
}

.btn-website {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.15s;
}

.btn-website:hover {
  background: #15803d;
  transform: translateY(-1px);
}

/* Footer */
.terms-footer {
  text-align: center;
  padding: 24px;
  font-size: 13px;
  color: #94a3b8;
  border-top: 1px solid #e8e5de;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.terms-footer p {
  margin: 0 0 4px;
}

.terms-footer p:last-child {
  margin-bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .terms-nav {
    padding: 0 16px;
  }
  
  .terms-main {
    padding: 32px 16px;
  }
  
  .terms-hero h1 {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .terms-content {
    padding: 24px;
  }
  
  .terms-section h3 {
    font-size: 18px;
  }
}

/* Dark Theme */
.terms-dark {
  background: #0c1018;
  color: #d1d4dc;
}

.terms-dark .terms-nav {
  background: rgba(12, 16, 24, 0.95);
  border-bottom-color: rgba(55, 59, 75, 0.5);
}

.terms-dark .terms-logo { color: #e5e7eb; }

.terms-dark .theme-toggle-btn {
  border-color: rgba(55, 59, 75, 0.6);
  color: #d1d4dc;
}

.terms-dark .theme-toggle-btn:hover {
  background: rgba(55, 59, 75, 0.4);
}

.terms-dark .btn-outline {
  border-color: rgba(55, 59, 75, 0.6);
  color: #d1d4dc;
}

.terms-dark .btn-outline:hover {
  border-color: #e5e7eb;
  color: #fff;
}

.terms-dark .terms-hero h1 { color: #e5e7eb; }

.terms-dark .hero-subtitle { color: #9ca3af; }

.terms-dark .terms-content {
  background: #121826;
  border-color: rgba(55, 59, 75, 0.5);
}

.terms-dark .intro-card {
  background: linear-gradient(135deg, rgba(41, 98, 255, 0.1) 0%, rgba(41, 98, 255, 0.05) 100%);
  border-color: rgba(41, 98, 255, 0.3);
}

.terms-dark .intro-card h2 { color: #e5e7eb; }

.terms-dark .intro-card p { color: #9ca3af; }

.terms-dark .terms-section h3 { color: #e5e7eb; }

.terms-dark .terms-section p { color: #cbd5e1; }

.terms-dark .terms-section strong { color: #e5e7eb; }

.terms-dark .list-content { color: #cbd5e1; }

.terms-dark .warning-box {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

.terms-dark .warning-box h4 { color: #fcd34d; }

.terms-dark .warning-box p { color: #fcd34d; }

.terms-dark .website-card {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.terms-dark .website-card h3 { color: #86efac; }

.terms-dark .website-card p { color: #4ade80; }

.terms-dark .terms-footer {
  border-top-color: rgba(55, 59, 75, 0.4);
  color: #64748b;
}
</style>
